import type { AppDispatch } from '../../data/store';
import type { WebSocketSessionInfo } from './webSocketService';
import * as passengerSlice from '../../data/passenger.slice';
import {
  StandingOrderEventType,
  type StandingOrderEventDetail,
} from '../../data/events/standing-order';

/**
 * Handles standing-order events. StandingOrderCancelled is the backend's
 * explicit end-of-cascade marker for a full STO cancel (measured on dev: it
 * arrives after the per-occurrence ReservationCanceled/TripLegCanceled events,
 * with standingOrderUUID in its payload). It deterministically confirms the
 * optimistic-cancel suppression — including for passengers whose future list
 * spans multiple pages, where the zero-occurrence page heuristic can never
 * fire.
 *
 * It deliberately does NOT schedule a list refetch of its own. Once the
 * cascade is complete the optimistically-removed list already matches server
 * truth, so nothing needs reloading; and the per-occurrence events that
 * precede (and, per the on-device capture, continue after) this marker already
 * drive the debounced refetch. Keeping the marker to a single concern is what
 * lets the reservation-event channel keep its narrow type, so no other portal
 * sees standing-order events.
 *
 * applyStandingOrderCancelMarker owns what confirmation entails (discarding
 * in-flight future-list requests whose snapshots predate the cascade's end,
 * then lifting the suppression) so the ordering is not this handler's problem.
 *
 * StandingOrderCancelRequested (the cascade-start marker) is ignored: the
 * cancel API's success callback already handles the optimistic side for the
 * session that initiated it.
 */
function updateStandingOrder(
  sessionInfo: WebSocketSessionInfo,
  dispatch: AppDispatch,
  detailType: StandingOrderEventType,
  detail: StandingOrderEventDetail,
) {
  if (detailType !== StandingOrderEventType.StandingOrderCancelled) return;
  if (!detail?.standingOrderUUID) return;

  dispatch(
    passengerSlice.applyStandingOrderCancelMarker(detail.standingOrderUUID),
  );
}

export { updateStandingOrder };
